import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, TextField } from '@ann/web-components';
import { useStore } from '../../../../../hooks';
import { useToast } from '../../../../../components/toast';
import { ErrorMessage } from '../RenameFolderModal/RenameFolderModal.styled';

const RenameDocumentModal = ({
    size = 'medium',
    modalWidth = '500px',
    isOpen,
    onClose,
    onSave,
    documentUuid,
    initialName = '',
    disabled = false,
}) => {
    const { documentsStore } = useStore();
    const toast = useToast();

    const [documentName, setDocumentName] = useState(initialName);
    const [touched, setTouched] = useState(false);

    const trimmedName = documentName.trim();
    const isFieldEmpty = trimmedName.length === 0;
    const showError = touched && isFieldEmpty;

    useEffect(() => {
        if (isOpen) {
            setDocumentName(initialName);
            setTouched(false);
        }
    }, [isOpen, initialName]);

    const handleSave = async () => {
        try {
            const payload = {
                document_uuid: documentUuid,
                new_name: trimmedName,
            };

            const result = await documentsStore.renameDocument(payload);

            if (documentsStore.isErrorRenameDocument) {
                toast.error({
                    message: documentsStore.errorRenameDocumentMessage || 'Error renaming document.',
                    canClose: true,
                });

                if (onSave) {
                    onSave({
                        success: false,
                        errorMessage: documentsStore.errorRenameDocumentMessage,
                    });
                }

                return;
            }

            toast.success({
                message: 'Document renamed successfully.',
                canClose: true,
            });

            if (onSave) {
                onSave({
                    success: true,
                    documentUuid,
                    newName: payload.new_name,
                    updatedDocument: result ?? { documentUuid, name: payload.new_name },
                });
            }

            handleClose();
        } catch (error) {
            console.error('Error saving document rename:', error);

            toast.error({
                message: documentsStore.errorRenameDocumentMessage || 'Network error. Please check your connection.',
                canClose: true,
            });

            if (onSave) {
                onSave({
                    success: false,
                    errorMessage: documentsStore.errorRenameDocumentMessage,
                });
            }
        }
    };

    const handleClose = () => {
        setDocumentName(initialName);
        setTouched(false);
        if (onClose) {
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
            <Modal.Header>
                <Modal.Header.Title>Rename document</Modal.Header.Title>
            </Modal.Header>

            <Modal.Body>
                <TextField
                    label="Document Name"
                    size="small"
                    placeholder="Untitled document"
                    value={documentName}
                    onChange={(value) => {
                        setDocumentName(value);
                        if (!touched && value.length > 0) {
                            setTouched(true);
                        }
                    }}
                    autoFocus
                    required
                />

                {showError && <ErrorMessage>Document name is required</ErrorMessage>}
            </Modal.Body>

            <Modal.Actions align="end">
                <Modal.Button
                    label="Cancel"
                    type="secondary"
                    onClick={handleClose}
                />
                <Modal.Button
                    label={documentsStore.isRenamingDocument ? 'Saving...' : 'Save'}
                    onClick={handleSave}
                    disabled={disabled || documentsStore.isRenamingDocument || isFieldEmpty}
                />
            </Modal.Actions>
        </Modal>
    );
};

export default observer(RenameDocumentModal);

